import { useState, useEffect, Fragment } from "react";
import FleetWebApi from "../Api/FleetWebApi";
import CircularProgress from "@mui/material/CircularProgress";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import TextField from "@mui/material/TextField";
import OnlineArchiveWarning, {
  isInOnlineArchive,
  useOnlineArchiveConfirm,
} from "./OnlineArchiveWarning";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  IconButton,
  Box,
  Typography,
  Grid,
  TableSortLabel,
  Tooltip,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { NavLink } from "react-router-dom";
import Moment from "react-moment";
import { CSVLink } from "react-csv";

function DiscrepancyRow({ row }) {
  const [open, setOpen] = useState(false);

  return (
    <Fragment>
      <TableRow sx={{ "& > *": { borderBottom: "unset" } }}>
        <TableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          <NavLink to={"/device/" + row.coreID} className="Nav_link">
            {row.coreID}
          </NavLink>
        </TableCell>
        <TableCell sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          <Tooltip title={row.deviceName || ""} placement="top">
            <span>{row.deviceName}</span>
          </Tooltip>
        </TableCell>
        <TableCell>{row.deviceType}</TableCell>
        <TableCell sx={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          <Tooltip title={row.ownerEmail || ""} placement="top">
            <span>{row.ownerEmail}</span>
          </Tooltip>
        </TableCell>
        <TableCell align="right">{row.avgCellBars != null ? row.avgCellBars.toFixed(2) : "-"}</TableCell>
        <TableCell align="right">{row.totalLogs}</TableCell>
        <TableCell align="right">{row.totalVitals}</TableCell>
        <TableCell align="right">{row.logsPercentage != null ? row.logsPercentage.toFixed(2) + "%" : "-"}</TableCell>
        <TableCell align="right">{row.vitalsPercentage != null ? row.vitalsPercentage.toFixed(2) + "%" : "-"}</TableCell>
        <TableCell align="right">
          {row.discrepancyTimestamps ? row.discrepancyTimestamps.length : 0}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={11}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 2 }}>
              <Typography variant="h6" gutterBottom component="div">
                Discrepancy Details
              </Typography>
              <Table size="small" aria-label="discrepancies" sx={{ tableLayout: 'auto' }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Timestamp</TableCell>
                    <TableCell align="right">Log Count</TableCell>
                    <TableCell align="right">Vital Count</TableCell>
                    <TableCell align="right">Connection Count</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {row.discrepancyTimestamps &&
                    row.discrepancyTimestamps.map((disc, index) => (
                      <TableRow key={index}>
                        <TableCell>{disc.timestamp}</TableCell>
                        <TableCell align="right">{disc.logCount}</TableCell>
                        <TableCell align="right">{disc.vitalCount}</TableCell>
                        <TableCell align="right">{disc.connectionCount}</TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </Fragment>
  );
}

function LogsVsVitals(props) {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reportDate, setReportDate] = useState(new Date());
  const [reportTimestamp, setReportTimestamp] = useState(new Date());
  const [orderBy, setOrderBy] = useState("avgCellBars");
  const [order, setOrder] = useState("desc");

  const { confirmDateChange, archiveDialog } = useOnlineArchiveConfirm();
  // Dates older than the archive cutoff only apply after the user confirms the archive query
  const handleDateChange = (date) => {
    confirmDateChange(date, setReportDate);
  };

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const sortData = (array, comparator) => {
    const stabilizedArray = array.map((el, index) => [el, index]);
    stabilizedArray.sort((a, b) => {
      const orderResult = comparator(a[0], b[0]);
      if (orderResult !== 0) return orderResult;
      return a[1] - b[1];
    });
    return stabilizedArray.map((el) => el[0]);
  };

  const getComparator = (order, orderBy) => {
    return order === "desc"
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  };

  const descendingComparator = (a, b, orderBy) => {
    let aVal = a[orderBy];
    let bVal = b[orderBy];

    // Handle null/undefined
    if (aVal == null) aVal = "";
    if (bVal == null) bVal = "";

    // For discrepancy count
    if (orderBy === "discrepancyCount") {
      aVal = a.discrepancyTimestamps ? a.discrepancyTimestamps.length : 0;
      bVal = b.discrepancyTimestamps ? b.discrepancyTimestamps.length : 0;
    }

    // Convert to appropriate type for comparison
    const aNum = parseFloat(aVal);
    const bNum = parseFloat(bVal);
    const isNumeric = !isNaN(aNum) && !isNaN(bNum);

    if (isNumeric) {
      if (bVal < aVal) return -1;
      if (bVal > aVal) return 1;
      return 0;
    } else {
      return bVal.toString().localeCompare(aVal.toString());
    }
  };

  const getSortedData = () => {
    if (orderBy) {
      return sortData(data, getComparator(order, orderBy));
    }
    return data;
  };

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);
    setError(null);
    
    if (reportDate) {
      // Archived dates only get here after the user confirmed the archive prompt
      FleetWebApi.getReportByType(
        "logs-vs-vitals-discrepancies",
        Math.floor(reportDate.getTime() / 1000),
        isInOnlineArchive(reportDate)
      )
        .then((response) => {
          if (isMounted) {
            setData(response.data);
            setReportTimestamp(response.timestamp);
            setIsLoading(false);
          }
        })
        .catch((error) => {
          if (isMounted) {
            setError(error.message);
            setIsLoading(false);
          }
        });
    }
    
    return () => {
      isMounted = false;
    };
  }, [reportDate]);

  // Prepare flat CSV data
  const getCsvData = () => {
    const flatData = [];
    data.forEach((device) => {
      if (device.discrepancyTimestamps && device.discrepancyTimestamps.length > 0) {
        device.discrepancyTimestamps.forEach((disc) => {
          flatData.push({
            coreID: device.coreID,
            deviceName: device.deviceName,
            deviceType: device.deviceType,
            ownerEmail: device.ownerEmail,
            avgCellBars: device.avgCellBars,
            totalLogs: device.totalLogs,
            totalVitals: device.totalVitals,
            logsPercentage: device.logsPercentage,
            vitalsPercentage: device.vitalsPercentage,
            discrepancyTimestamp: disc.timestamp,
            logCount: disc.logCount,
            vitalCount: disc.vitalCount,
            connectionCount: disc.connectionCount,
          });
        });
      } else {
        flatData.push({
          coreID: device.coreID,
          deviceName: device.deviceName,
          deviceType: device.deviceType,
          ownerEmail: device.ownerEmail,
          avgCellBars: device.avgCellBars,
          totalLogs: device.totalLogs,
          totalVitals: device.totalVitals,
          logsPercentage: device.logsPercentage,
          vitalsPercentage: device.vitalsPercentage,
          discrepancyTimestamp: "",
          logCount: "",
          vitalCount: "",
          connectionCount: "",
        });
      }
    });
    return flatData;
  };

  return (
    <Fragment>
      <h1>Logs Vs Vitals Discrepancies</h1>

      <Grid container spacing={2}>
        <Grid item xs={12} md={12} lg={6}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              disableToolbar
              variant="inline"
              format="MM/dd/yyyy"
              margin="normal"
              id="date-picker-inline"
              label="Report Date"
              value={reportDate}
              onChange={handleDateChange}
              KeyboardButtonProps={{
                "aria-label": "change date",
              }}
              renderInput={(params) => <TextField {...params} />}
            />
          </LocalizationProvider>
          <OnlineArchiveWarning date={reportDate} />
          {archiveDialog}
          {!isLoading && reportTimestamp && (
            <h3>
              <Moment format="YYYY-MM-DD HH:mm:ss Z">{reportTimestamp}</Moment>
            </h3>
          )}
        </Grid>
        <Grid item xs={12} md={12} lg={6} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
          {!isLoading && data.length > 0 && (
            <h3>
              <CSVLink data={getCsvData()} filename="logs-vs-vitals-discrepancies.csv">
                Download CSV
              </CSVLink>
            </h3>
          )}
        </Grid>
      </Grid>

      {error && (
        <Typography color="error" sx={{ p: 2 }}>
          Error Loading Data: {error}
        </Typography>
      )}
      
      {!error && isLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress color="secondary" />
        </Box>
      )}
      
      {!error && !isLoading && (
        <Paper sx={{ m: 2 }}>
          <TableContainer>
            <Table aria-label="collapsible table" size="small">
              <TableHead>
                <TableRow>
                  <TableCell />
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === "coreID"}
                      direction={orderBy === "coreID" ? order : "asc"}
                      onClick={() => handleSort("coreID")}
                    >
                      Core ID
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === "deviceName"}
                      direction={orderBy === "deviceName" ? order : "asc"}
                      onClick={() => handleSort("deviceName")}
                    >
                      Device Name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === "deviceType"}
                      direction={orderBy === "deviceType" ? order : "asc"}
                      onClick={() => handleSort("deviceType")}
                    >
                      Device Type
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === "ownerEmail"}
                      direction={orderBy === "ownerEmail" ? order : "asc"}
                      onClick={() => handleSort("ownerEmail")}
                    >
                      Owner Email
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={orderBy === "avgCellBars"}
                      direction={orderBy === "avgCellBars" ? order : "asc"}
                      onClick={() => handleSort("avgCellBars")}
                    >
                      Avg Cell Bars
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={orderBy === "totalLogs"}
                      direction={orderBy === "totalLogs" ? order : "asc"}
                      onClick={() => handleSort("totalLogs")}
                    >
                      Total Logs
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={orderBy === "totalVitals"}
                      direction={orderBy === "totalVitals" ? order : "asc"}
                      onClick={() => handleSort("totalVitals")}
                    >
                      Total Vitals
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={orderBy === "logsPercentage"}
                      direction={orderBy === "logsPercentage" ? order : "asc"}
                      onClick={() => handleSort("logsPercentage")}
                    >
                      Logs %
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={orderBy === "vitalsPercentage"}
                      direction={orderBy === "vitalsPercentage" ? order : "asc"}
                      onClick={() => handleSort("vitalsPercentage")}
                    >
                      Vitals %
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel
                      active={orderBy === "discrepancyCount"}
                      direction={orderBy === "discrepancyCount" ? order : "asc"}
                      onClick={() => handleSort("discrepancyCount")}
                    >
                      Discrepancies
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getSortedData().map((row, index) => (
                  <DiscrepancyRow key={index} row={row} />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {data.length === 0 && (
            <Box sx={{ p: 3, textAlign: "center" }}>
              <Typography>No discrepancies found for this date.</Typography>
            </Box>
          )}
        </Paper>
      )}
    </Fragment>
  );
}

export default LogsVsVitals;
